import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'

/**
 * Workspace presence (online/offline) — CP1 plumbing, no UI.
 *
 * Uses Supabase Realtime Presence (table-free, ephemeral): every authenticated
 * user joins one app-wide channel `presence:workspace:{workspaceId}` and
 * track()s themselves once SUBSCRIBED. The realtime layer handles join/leave on
 * disconnect, so there is no heartbeat, no presence table, and no stale cleanup.
 *
 * The presence key is the user_id, so a user with multiple tabs/devices collapses
 * to ONE entry — presenceState() has one key per online user. onlineUserIds is
 * the set of those keys.
 *
 * CP2 renders the dots (it consumes usePresence()); this file is data only.
 */

interface PresenceCtx {
  /** user_ids currently online in the workspace. */
  onlineUserIds: Set<string>
}

// Default to "nobody online" so a consumer rendered outside the provider (or
// while logged out) degrades gracefully rather than throwing — presence is a
// decorative signal, not a hard dependency.
const PresenceContext = createContext<PresenceCtx>({ onlineUserIds: new Set() })

export function usePresence(): PresenceCtx {
  return useContext(PresenceContext)
}

export function PresenceProvider({ children }: { children: ReactNode }) {
  const { profile, workspace } = useAuth()
  const workspaceId = workspace?.id
  const profileId = profile?.id
  const [onlineUserIds, setOnlineUserIds] = useState<Set<string>>(() => new Set())

  useEffect(() => {
    // Logged out / no workspace → no channel, nobody online. (Guard ensures we
    // never mount presence above auth.)
    if (!workspaceId || !profileId) {
      setOnlineUserIds(new Set())
      return
    }

    const channel = supabase.channel(`presence:workspace:${workspaceId}`, {
      config: { presence: { key: profileId } },
    })

    // presenceState() keys are the presence keys (= user_ids). Re-derive the
    // online set on every presence change; 'sync' alone covers it, but join/leave
    // keep it crisp.
    const syncOnline = () => {
      setOnlineUserIds(new Set(Object.keys(channel.presenceState())))
    }

    channel
      .on('presence', { event: 'sync' }, syncOnline)
      .on('presence', { event: 'join' }, syncOnline)
      .on('presence', { event: 'leave' }, syncOnline)
      .subscribe((status) => {
        // CRITICAL: track() only after SUBSCRIBED — tracking earlier is dropped.
        if (status === 'SUBSCRIBED') {
          channel
            .track({ user_id: profileId, online_at: new Date().toISOString() })
            .catch(() => { /* transient — next sync reconciles */ })
        }
      })

    // Cleanup on unmount / workspace / profile change (and sign-out, which clears
    // profile): untrack so we don't linger as online, then drop the channel.
    return () => {
      channel.untrack().catch(() => { /* ignore */ })
      supabase.removeChannel(channel).catch(() => { /* ignore */ })
    }
  }, [workspaceId, profileId])

  const value = useMemo<PresenceCtx>(() => ({ onlineUserIds }), [onlineUserIds])

  return <PresenceContext.Provider value={value}>{children}</PresenceContext.Provider>
}
